// components/KioskSetup.jsx
import React, { useState, useEffect } from 'react';
import { setKioskScreenId, getKioskApiBase } from '../utils/kioskConfig';

/**
 * Kiosk Setup Screen
 * Shown when screen_id is not configured
 * Allows selecting from available screens
 */
export default function KioskSetup({ onConfigured }) {
  const [screens, setScreens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedScreenId, setSelectedScreenId] = useState(null);

  useEffect(() => {
    loadScreens();
  }, []);

  async function loadScreens() {
    try {
      setLoading(true);
      setError(null);
      
      const apiBase = getKioskApiBase();
      const response = await fetch(`${apiBase}/rcc-api/advertising/screens?is_active=true`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      setScreens(data);
    } catch (err) {
      console.error('Failed to load screens:', err);
      setError(err.message || 'Failed to load screens');
    } finally {
      setLoading(false);
    }
  }

  function handleSelectScreen(screenId) {
    setKioskScreenId(screenId);
    if (onConfigured) {
      onConfigured(screenId);
    } else {
      // Reload page to apply configuration
      window.location.reload();
    }
  }

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.spinner}></div>
          <h2 style={styles.title}>Loading...</h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <span style={{ fontSize: '64px' }}>⚠️</span>
          <h2 style={styles.title}>Configuration Error</h2>
          <p style={styles.errorText}>{error}</p>
          <button onClick={loadScreens} style={styles.retryButton}>
            Retry
          </button>
          <div style={styles.help}>
            <p style={styles.helpText}>You can also configure manually via URL:</p>
            <code style={styles.code}>
              ?kiosk=1&screen_id=YOUR_SCREEN_ID
            </code>
          </div>
        </div>
      </div>
    );
  }

  if (screens.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <span style={{ fontSize: '64px' }}>🖥️</span>
          <h2 style={styles.title}>No Screens Available</h2>
          <p style={styles.subtitle}>Create screens in the admin panel first</p>
          <div style={styles.help}>
            <p style={styles.helpText}>Or configure manually via URL:</p>
            <code style={styles.code}>
              ?kiosk=1&screen_id=YOUR_SCREEN_ID
            </code>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h2 style={styles.title}>🖥️ Kiosk Configuration</h2>
        <p style={styles.subtitle}>Select which screen this kiosk represents</p>
        
        <div style={styles.screensList}>
          {screens.map(screen => (
            <button
              key={screen.screen_id}
              onClick={() => handleSelectScreen(screen.screen_id)}
              onMouseEnter={() => setSelectedScreenId(screen.screen_id)}
              onMouseLeave={() => setSelectedScreenId(null)}
              style={{
                ...styles.screenButton,
                ...(selectedScreenId === screen.screen_id ? styles.screenButtonHover : {})
              }}
            >
              <div style={styles.screenInfo}>
                <h3 style={styles.screenName}>{screen.screen_name}</h3>
                {screen.location_description && (
                  <p style={styles.screenLocation}>📍 {screen.location_description}</p>
                )}
                <p style={styles.screenResolution}>
                  {screen.resolution_width}×{screen.resolution_height}
                  {screen.resolution_height > screen.resolution_width ? ' (Portrait)' : ' (Landscape)'}
                </p>
              </div>
              <div style={styles.arrow}>→</div>
            </button>
          ))}
        </div>

        <div style={styles.footer}>
          <p style={styles.footerText}>Configuration will be saved to this device</p>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    width: '100vw',
    height: '100vh',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px'
  },
  card: {
    background: 'white',
    borderRadius: '16px',
    padding: '40px',
    maxWidth: '600px',
    width: '100%',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    textAlign: 'center'
  },
  title: {
    fontSize: '32px',
    fontWeight: '700',
    color: '#1e293b',
    margin: '16px 0 8px'
  },
  subtitle: {
    fontSize: '16px',
    color: '#64748b',
    margin: '0 0 32px'
  },
  screensList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginBottom: '24px'
  },
  screenButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px',
    background: '#f8fafc',
    border: '2px solid #e2e8f0',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    textAlign: 'left'
  },
  screenButtonHover: {
    background: '#eff6ff',
    borderColor: '#3b82f6',
    transform: 'translateX(4px)',
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)'
  },
  screenInfo: {
    flex: 1
  },
  screenName: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#1e293b',
    margin: '0 0 8px'
  },
  screenLocation: {
    fontSize: '14px',
    color: '#64748b',
    margin: '4px 0'
  },
  screenResolution: {
    fontSize: '13px',
    color: '#94a3b8',
    margin: '4px 0 0'
  },
  arrow: {
    fontSize: '24px',
    color: '#3b82f6',
    fontWeight: 'bold'
  },
  footer: {
    paddingTop: '24px',
    borderTop: '1px solid #e2e8f0'
  },
  footerText: {
    fontSize: '13px',
    color: '#94a3b8',
    margin: 0
  },
  spinner: {
    width: '60px',
    height: '60px',
    border: '4px solid #e2e8f0',
    borderTopColor: '#3b82f6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 20px'
  },
  errorText: {
    fontSize: '16px',
    color: '#ef4444',
    margin: '16px 0'
  },
  retryButton: {
    padding: '12px 24px',
    background: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background 0.2s'
  },
  help: {
    marginTop: '24px',
    padding: '16px',
    background: '#f8fafc',
    borderRadius: '8px',
    textAlign: 'left'
  },
  helpText: {
    fontSize: '13px',
    color: '#64748b',
    margin: '0 0 8px'
  },
  code: {
    display: 'block',
    padding: '8px 12px',
    background: '#1e293b',
    color: '#10b981',
    borderRadius: '4px',
    fontSize: '12px',
    fontFamily: 'monospace',
    wordBreak: 'break-all'
  }
};
